import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Copy,
  Expand,
  Loader2,
  Mic,
  PhoneIncoming,
  PhoneOutgoing,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import TranscriptAudioPlayer from "@/components/common/TranscriptAudioPlayer";
import SummaryFormattedContent from "@/components/transcripts/SummaryFormattedContent";
import { escapeRegExp } from "@/utils/transcriptHelpers";
import {
  enrichSegmentsWithTiming,
  findActiveSegmentIndex,
  getSpeakerMeta,
  groupSegmentsBySpeaker,
  parseTranscriptSegments,
  speakerBadgeClass,
  speakerBubbleClass,
  speakerNameClass,
} from "@/utils/transcriptHelpers";
import CallPartyPhone from "@/components/common/CallPartyPhone";
import { getCustomerPhoneFromRecord, normalizeTurkishPhone } from "@/utils/callPartyHelpers";

const HighlightText = ({ text, query, isDark }) => {
  if (!text) return null;
  if (!query?.trim()) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(query.trim())})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.trim().toLowerCase() ? (
      <mark key={i} className={`${isDark ? "bg-amber-500/35 text-amber-100" : "bg-amber-200 text-amber-950"} rounded px-0.5`}>{part}</mark>
    ) : part
  );
};

const statusBadgeClass = (status, isDark) => {
  if (status === "completed") return isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (status === "processing" || status === "pending") return isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-100 text-amber-800 border-amber-200";
  if (status === "failed") return isDark ? "bg-rose-500/20 text-rose-300 border-rose-500/30" : "bg-rose-100 text-rose-700 border-rose-200";
  return isDark ? "bg-slate-700/50 text-slate-300 border-slate-600" : "bg-gray-100 text-gray-600 border-gray-200";
};

const formatDuration = (seconds) => {
  if (!seconds) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins === 0 ? `${secs}s` : `${mins}dk ${secs}s`;
};

const TranscriptDetailView = ({
  record,
  agents = [],
  leadStatuses,
  customerProfileCache = {},
  customerProfileLoadingKeys = {},
  onCustomerClick,
  onCustomerHover,
  isDark,
  textClass,
  labelClass,
  inputClass,
  searchQuery,
  onSearchChange,
  onCopy,
  onStart,
  onRetry,
  onPoll,
  transcriptPolling,
  isTranscriptInProgress,
  getTranscriptStatusLabel,
  onClose,
  onDelete,
}) => {
  const audioRef = useRef(null);
  const chatScrollRef = useRef(null);
  const segmentRefs = useRef({});
  const lastScrolledTimedIndexRef = useRef(-1);

  const [audioTime, setAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(record.konusma_suresi || 0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  const rawSegments = useMemo(() => parseTranscriptSegments(record), [record]);
  const timedSegments = useMemo(
    () => enrichSegmentsWithTiming(rawSegments, audioDuration || record.konusma_suresi || 0),
    [rawSegments, audioDuration, record.konusma_suresi]
  );

  const speakerGroups = useMemo(() => groupSegmentsBySpeaker(rawSegments), [rawSegments]);

  const filteredSegments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return timedSegments;
    return timedSegments.filter((seg) => seg.text.toLowerCase().includes(q) || seg.speaker.toLowerCase().includes(q));
  }, [timedSegments, searchQuery]);

  const activeSegmentIndex = useMemo(() => findActiveSegmentIndex(timedSegments, audioTime), [timedSegments, audioTime]);
  const activeSegment = activeSegmentIndex >= 0 ? timedSegments[activeSegmentIndex] : null;

  useEffect(() => {
    segmentRefs.current = {};
    lastScrolledTimedIndexRef.current = -1;
    setAudioTime(0);
    setIsAudioPlaying(false);
    setSummaryModalOpen(false);
  }, [record?.id, record?.cdr_id]);

  useEffect(() => {
    if (!isAudioPlaying || activeSegmentIndex < 0) return;
    if (lastScrolledTimedIndexRef.current === activeSegmentIndex) return;

    const activeSegment = timedSegments[activeSegmentIndex];
    const idx = filteredSegments.findIndex(
      (seg) => seg.startSeconds === activeSegment?.startSeconds && seg.text === activeSegment?.text
    );
    if (idx < 0) return;

    const el = segmentRefs.current[idx];
    if (!el) return;

    lastScrolledTimedIndexRef.current = activeSegmentIndex;
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeSegmentIndex, timedSegments, filteredSegments, isAudioPlaying]);

  const handleSegmentClick = useCallback((seg) => {
    if (seg.startSeconds == null) return;
    audioRef.current?.seekTo(seg.startSeconds);
    if (!isAudioPlaying) {
      audioRef.current?.play();
    }
  }, [isAudioPlaying]);

  const shellClass = isDark
    ? "rounded-2xl border border-slate-800/80 bg-slate-950/60 shadow-xl shadow-black/20"
    : "rounded-2xl border border-gray-200 bg-white shadow-sm";

  const yonInfo = record.yon === 2
    ? { label: "Giden", icon: PhoneOutgoing, className: isDark ? "text-blue-300 bg-blue-500/15 border-blue-500/25" : "text-blue-700 bg-blue-50 border-blue-100" }
    : record.yon === 1
      ? { label: "Gelen", icon: PhoneIncoming, className: isDark ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/25" : "text-emerald-700 bg-emerald-50 border-emerald-100" }
      : null;

  const hasTranscript = !!record.transcript_text || rawSegments.length > 0;
  const inProgress = isTranscriptInProgress(record);
  const YonIcon = yonInfo?.icon;
  const hasAudio = !!(record.recording || record.recording_url);
  const summaryText = (record.summary_text || "").trim();
  const hasSummary = summaryText.length > 0;
  const summaryPlaceholder = hasTranscript
    ? "Bu görüşme için özet oluşturulmadı."
    : "Transkript tamamlandığında özet burada görünecek.";

  const customerPhone = useMemo(() => getCustomerPhoneFromRecord(record, agents), [record, agents]);
  const customerKey = customerPhone ? normalizeTurkishPhone(customerPhone) : "";
  const cachedCustomer = customerKey ? customerProfileCache[customerKey] : null;
  const customerLoading = !!customerProfileLoadingKeys[customerKey];

  const callPartyProps = {
    record,
    agents,
    isDark,
    leadStatuses,
    customerProfile: cachedCustomer,
    customerProfileLoading: customerLoading,
    onCustomerClick,
    onHoverRequest: onCustomerHover,
    showRoleLabel: true,
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className={`h-8 shrink-0 -ml-2 ${isDark ? "text-slate-400 hover:text-white hover:bg-slate-800" : "text-gray-600"}`}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Transkriptler
          </Button>
          <span className={`text-sm ${labelClass}`}>/</span>
          <span className={`text-sm font-medium truncate max-w-[200px] sm:max-w-none ${textClass}`}>
            {record.agent_name || record.tarih?.split(" ")[0] || "Detay"}
          </span>
          <Badge className={`border text-[10px] h-5 px-1.5 ${statusBadgeClass(record.status, isDark)}`}>
            {getTranscriptStatusLabel(record.status)}
          </Badge>
          {yonInfo && YonIcon && (
            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${yonInfo.className}`}>
              <YonIcon className="w-3 h-3" />
              {yonInfo.label}
            </span>
          )}
          <span className={`inline-flex items-center gap-1 text-xs font-mono ${labelClass}`}>
            <Calendar className="w-3 h-3 shrink-0" />
            {record.tarih || "-"}
          </span>
          <span className={`inline-flex items-center gap-1 text-xs font-mono ${labelClass}`}>
            <Clock className="w-3 h-3 shrink-0" />
            {formatDuration(record.konusma_suresi)}
          </span>
          {record.agent_name && (
            <span className={`inline-flex items-center gap-1 text-xs ${labelClass}`}>
              <User className="w-3 h-3 shrink-0" />
              {record.agent_name}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className={`h-8 shrink-0 ${isDark ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" : "text-rose-600 hover:text-rose-700 hover:bg-rose-50"}`}
          title="Transkripti sil"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        <div className="xl:col-span-4 space-y-4 xl:sticky xl:top-4">
          <div className={`${shellClass} p-4 space-y-2`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className={`w-4 h-4 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                <h3 className={`text-sm font-semibold ${textClass}`}>AI Özet</h3>
              </div>
              {hasSummary && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSummaryModalOpen(true)}
                  className={`h-7 px-2 text-[10px] shrink-0 ${isDark ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" : "text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"}`}
                >
                  <Expand className="w-3 h-3 mr-1" />
                  Genişlet
                </Button>
              )}
            </div>
            <button
              type="button"
              disabled={!hasSummary}
              onClick={() => hasSummary && setSummaryModalOpen(true)}
              className={`w-full text-left rounded-lg transition-colors ${
                hasSummary
                  ? (isDark ? "hover:bg-slate-900/50 cursor-pointer" : "hover:bg-gray-50 cursor-pointer")
                  : "cursor-default"
              }`}
            >
              <div
                className={`max-h-28 overflow-y-auto pr-1 ${!hasSummary ? "opacity-70" : ""}`}
              >
                {hasSummary ? (
                  <SummaryFormattedContent text={summaryText} isDark={isDark} compact />
                ) : (
                  <p className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                    {summaryPlaceholder}
                  </p>
                )}
              </div>
              {hasSummary && (
                <p className={`text-[10px] mt-1.5 ${isDark ? "text-emerald-400/80" : "text-emerald-600"}`}>
                  Tamamını görmek için tıklayın
                </p>
              )}
            </button>
          </div>

          <Dialog open={summaryModalOpen} onOpenChange={setSummaryModalOpen}>
            <DialogContent className={`max-w-2xl w-[calc(100%-2rem)] max-h-[min(85vh,720px)] !flex !flex-col gap-0 p-0 overflow-hidden sm:rounded-2xl ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
              <DialogHeader className={`px-6 pt-6 pb-4 pr-14 border-b shrink-0 text-left ${isDark ? "border-slate-800" : "border-gray-100"}`}>
                <DialogTitle className={`flex items-center gap-2.5 text-base font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${isDark ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30" : "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"}`}>
                    <Sparkles className="w-4 h-4" />
                  </span>
                  AI Özet
                </DialogTitle>
                <DialogDescription className={`text-xs mt-1.5 ${labelClass}`}>
                  {[record.tarih, record.agent_name].filter(Boolean).join(" · ") || "Görüşme özeti"}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                <div className={`rounded-xl border px-5 py-4 ${isDark ? "border-slate-800 bg-slate-950/60" : "border-gray-100 bg-gray-50/80"}`}>
                  <SummaryFormattedContent text={summaryText} isDark={isDark} />
                </div>
              </div>
              <div className={`shrink-0 flex justify-end gap-2 px-6 py-4 border-t ${isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-100 bg-gray-50/80"}`}>
                {hasTranscript && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onCopy(summaryText)}
                    className={`h-8 ${isDark ? "border-slate-700" : ""}`}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                    Kopyala
                  </Button>
                )}
                <Button size="sm" onClick={() => setSummaryModalOpen(false)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                  Kapat
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className={`${shellClass} p-4 space-y-2`}>
            <h3 className={`text-sm font-semibold ${textClass}`}>Çağrı Bilgisi</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className={`rounded-xl px-3 py-2.5 ${isDark ? "bg-slate-900/70" : "bg-gray-50"}`}>
                <p className={`text-[10px] uppercase tracking-wide ${labelClass}`}>Arayan</p>
                <div className="mt-1.5">
                  <CallPartyPhone phone={record.arayan_numara} {...callPartyProps} />
                </div>
              </div>
              <div className={`rounded-xl px-3 py-2.5 ${isDark ? "bg-slate-900/70" : "bg-gray-50"}`}>
                <p className={`text-[10px] uppercase tracking-wide ${labelClass}`}>Aranan</p>
                <div className="mt-1.5">
                  <CallPartyPhone phone={record.aranan_numara} {...callPartyProps} />
                </div>
              </div>
            </div>
          </div>

          {speakerGroups.length > 0 && (
            <div className={`${shellClass} p-4 space-y-2`}>
              <h3 className={`text-sm font-semibold ${textClass}`}>Konuşmacılar</h3>
              {speakerGroups.map((group) => (
                <div key={group.label} className={`rounded-lg border p-2.5 ${speakerBubbleClass(group.tone, isDark)}`}>
                  <div className="flex items-start gap-2">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold shrink-0 ${speakerBadgeClass(group.tone, isDark)}`}>
                      {group.badge}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-[11px] font-semibold ${speakerNameClass(group.tone, isDark)}`}>{group.label}</p>
                      <p className={`text-[11px] mt-0.5 leading-snug line-clamp-2 ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                        {group.preview}{group.preview.length >= 220 ? "..." : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <details className={`${shellClass} p-4 group`}>
            <summary className={`text-sm font-medium cursor-pointer list-none flex items-center justify-between ${textClass}`}>
              Teknik & İşlemler
              <span className={`text-xs ${labelClass} group-open:hidden`}>Göster</span>
            </summary>
            <div className="mt-3 space-y-2 text-xs">
              <div><span className={labelClass}>Servis: </span>{record.provider_label || "Gemini AI"}</div>
              {record.error_message && <div className="text-rose-500">{record.error_message}</div>}
              <div className="flex flex-wrap gap-2 pt-2">
                {(record.status === "none" || record.status === "failed" || !record.status) && (
                  <Button size="sm" onClick={onStart} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">Oluştur</Button>
                )}
                {record.status === "completed" && (
                  <Button size="sm" variant="outline" onClick={onRetry} className={`h-8 ${isDark ? "border-slate-700" : ""}`}>Yeniden İşle</Button>
                )}
                {inProgress && (
                  <Button size="sm" variant="ghost" onClick={onPoll} className="h-8">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${transcriptPolling ? "animate-spin" : ""}`} />
                    Yenile
                  </Button>
                )}
              </div>
            </div>
          </details>
        </div>

        {/* Sağ — konuşma + altta ses */}
        <div className={`${shellClass} xl:col-span-8 flex flex-col min-h-[520px] max-h-[calc(100vh-180px)] overflow-hidden`}>
          <div className={`shrink-0 flex flex-wrap items-center gap-2 px-3 py-2.5 border-b ${isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-100 bg-gray-50/60"}`}>
            <div className="relative flex-1 min-w-[180px]">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${labelClass}`} />
              <Input
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Transkriptte kelime ara..."
                className={`pl-8 h-8 text-sm rounded-lg ${inputClass}`}
              />
            </div>
            {hasTranscript && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onCopy(record.transcript_text || rawSegments.map((s) => s.text).join("\n"))}
                className={`h-8 rounded-lg text-xs ${isDark ? "border-slate-700" : ""}`}
              >
                <Copy className="w-3.5 h-3.5 mr-1" />
                Kopyala
              </Button>
            )}
            {isAudioPlaying && activeSegment && (
              <Badge className={isDark ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]" : "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"}>
                Senkron
              </Badge>
            )}
            {searchQuery && (
              <Badge className={`text-[10px] ${isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                {filteredSegments.length} eşleşme
              </Badge>
            )}
          </div>

          <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
            {inProgress && !hasTranscript ? (
              <div className={`flex flex-col items-center justify-center py-16 ${labelClass}`}>
                <Loader2 className="w-8 h-8 mb-2 animate-spin text-emerald-500" />
                <p className="text-sm">Transkript {getTranscriptStatusLabel(record.status).toLowerCase()}...</p>
              </div>
            ) : filteredSegments.length === 0 ? (
              <div className={`flex flex-col items-center justify-center py-16 ${labelClass}`}>
                <Mic className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">{searchQuery ? "Aranan kelime bulunamadı" : "Henüz transkript metni yok"}</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredSegments.map((seg, idx) => {
                  const meta = getSpeakerMeta(seg.speaker, idx);
                  const isAgent = meta.isAgent;
                  const prevMeta = idx > 0 ? getSpeakerMeta(filteredSegments[idx - 1].speaker, idx - 1) : null;
                  const sameSpeaker = prevMeta?.label === meta.label;
                  const isActive = !!activeSegment
                    && seg.startSeconds === activeSegment.startSeconds
                    && seg.text === activeSegment.text;
                  const canSeek = seg.startSeconds != null && hasAudio;

                  return (
                    <div
                      key={`${seg.startTime}-${idx}-${seg.text.slice(0, 12)}`}
                      ref={(el) => { segmentRefs.current[idx] = el; }}
                      className={`flex ${isAgent ? "justify-start" : "justify-end"} ${sameSpeaker ? "mt-0" : "mt-1.5"}`}
                    >
                      <button
                        type="button"
                        disabled={!canSeek}
                        onClick={() => handleSegmentClick(seg)}
                        className={`max-w-[94%] sm:max-w-[88%] text-left transition-all rounded-xl ${
                          canSeek ? "cursor-pointer" : "cursor-default"
                        } ${isAgent ? "pr-6 sm:pr-10" : "pl-6 sm:pl-10"} ${
                          isActive
                            ? (isDark ? "ring-2 ring-emerald-400/70 ring-offset-2 ring-offset-slate-950" : "ring-2 ring-emerald-500/60 ring-offset-2 ring-offset-white")
                            : ""
                        }`}
                      >
                        {!sameSpeaker && (
                          <div className={`flex items-center gap-1.5 mb-0.5 ${isAgent ? "" : "justify-end"}`}>
                            <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold ${speakerBadgeClass(meta.tone, isDark)}`}>
                              {meta.badge}
                            </span>
                            <span className={`text-[10px] font-semibold ${speakerNameClass(meta.tone, isDark)}`}>{meta.label}</span>
                            {seg.startTime && (
                              <span className={`text-[9px] font-mono ${labelClass}`}>{seg.startTime}</span>
                            )}
                          </div>
                        )}
                        <div className={`rounded-xl border px-2.5 py-1.5 text-[13px] leading-snug ${
                          isAgent ? "rounded-tl-sm" : "rounded-tr-sm"
                        } ${speakerBubbleClass(meta.tone, isDark)} ${
                          isActive ? (isDark ? "border-emerald-400/40 shadow-emerald-500/10 shadow-md" : "border-emerald-300 shadow-sm") : ""
                        }`}>
                          <HighlightText text={seg.text} query={searchQuery} isDark={isDark} />
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {hasAudio && (
            <div className={`shrink-0 border-t px-3 py-3 ${isDark ? "border-slate-800 bg-slate-900/70" : "border-gray-100 bg-gray-50/80"}`}>
              <div className="flex items-center gap-2 mb-2">
                <Mic className={`w-3.5 h-3.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                <span className={`text-xs font-medium ${textClass}`}>Ses Kaydı</span>
                <span className={`text-[10px] ${labelClass}`}>· oynatırken konuşma akar</span>
              </div>
              <TranscriptAudioPlayer
                ref={audioRef}
                audioUrl={record.recording || record.recording_url}
                isDark={isDark}
                duration={record.konusma_suresi}
                onTimeUpdate={setAudioTime}
                onPlayingChange={setIsAudioPlaying}
                onDurationChange={setAudioDuration}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriptDetailView;
